/**
 * space/fish-tank.js — the tank and everything swimming in it (GDD 9.3).
 *
 * Every fish in `save.prizes.fish` lives here permanently. The tank's tier
 * decides how many of them can be *shown* at once; the rest are waitlisted with
 * a prompt to upgrade. Nothing is ever deleted — GDD 9.3 is explicit that fish
 * are never lost.
 *
 * Swimming is a closed-form function of elapsed time rather than an integrated
 * simulation. That is deliberate: a pure `position(seed, t)` is reproducible
 * regardless of frame rate, and it never touches the shared seeded generator,
 * so a stall test running elsewhere cannot be desynced by a fish turning left.
 *
 * @module space/fish-tank
 */

import * as THREE from 'three';
import { TANK_TIERS, FISH_VARIANTS, STALL_BY_ID } from '../constants.js';
import * as factory from '../assets3d/factory.js';
import { createPanel } from '../ui/panels.js';
import { pointer, createLabel } from './pointer.js';
import { swimSeed, swimPosition, dartAmount, SplashPool } from './fish-swim.js';

/** @param {number} capacity @returns {object} The tier that capacity belongs to. */
export function tankTier(capacity) {
  return TANK_TIERS.find((t) => t.capacity === capacity)
    || TANK_TIERS.filter((t) => t.capacity <= capacity).pop()
    || TANK_TIERS[0];
}

/** @param {number} capacity @returns {object|null} The next purchasable tier. */
export function nextTankTier(capacity) {
  return TANK_TIERS.find((t) => t.capacity > capacity) || null;
}

/** @param {string} variant @returns {object} */
export function fishVariant(variant) {
  return FISH_VARIANTS.find((v) => v.id === variant) || FISH_VARIANTS[0];
}

/**
 * The tank, its fish, their labels and the info card.
 */
export class FishTank {
  /**
   * @param {{ room: import('./space-world.js').SpaceRoom, readOnly?: boolean,
   *           onUpgrade?: () => void, onRename?: (fishId: string) => void }} opts
   */
  constructor(opts) {
    this.room = opts.room;
    this.readOnly = !!opts.readOnly;
    this.onUpgrade = opts.onUpgrade;
    this.onRename = opts.onRename;

    this.group = new THREE.Group();
    this.group.name = 'space-tank';
    this.room.root.add(this.group);

    /** @type {Array<object>} */ this.fish = [];
    /** @type {Array<object>} */ this.waiting = [];
    /** @type {THREE.Object3D[]} */ this.decorAnchors = [];
    this.bounds = new THREE.Vector3(0.2, 0.14, 0.2);
    this.centre = new THREE.Vector3();
    this.tier = TANK_TIERS[0];

    this._elapsed = 0;
    this._label = null;
    this._card = null;
    this._cardFor = null;
    this._focus = null;
    this._splashes = new SplashPool(this.group);
    this._idPrefix = `${this.room.name}-tank`;
  }

  /**
   * @param {{ fish: object[], tankCapacity: number }} model
   */
  rebuild(model) {
    this.dispose();
    this.model = model;
    this.tier = tankTier(model.tankCapacity);

    const anchor = this.room.anchors.tank;
    this.group.position.copy(anchor.position);
    this.group.rotation.y = anchor.rotation.y;

    this._buildVessel();
    this._buildPlaques();
    this._stock(model.fish);
  }

  // ── Construction ──────────────────────────────────────────────────────────

  /** @private */
  _buildVessel() {
    const t = this.tier;
    const midY = t.standHeight + t.height / 2;
    this.centre.set(0, midY, 0);
    this.bounds.set(t.width / 2 - 0.08, t.height / 2 - 0.07, t.depth / 2 - 0.07);

    const stand = new THREE.Mesh(
      new THREE.BoxGeometry(t.width + 0.16, t.standHeight, t.depth + 0.12),
      new THREE.MeshStandardMaterial({ color: 0x5d4632, roughness: 0.85 }),
    );
    stand.position.y = t.standHeight / 2;
    stand.castShadow = true;
    stand.receiveShadow = true;
    this.group.add(stand);

    const water = new THREE.Mesh(
      t.id === 'bowl'
        ? new THREE.SphereGeometry(t.width / 2 - 0.03, 20, 14)
        : new THREE.BoxGeometry(t.width - 0.05, t.height - 0.08, t.depth - 0.05),
      new THREE.MeshStandardMaterial({
        color: 0x2f9fd6, transparent: true, opacity: 0.3, roughness: 0.12,
        depthWrite: false,
      }),
    );
    water.position.y = midY;
    water.renderOrder = 1;
    this.group.add(water);

    const glass = new THREE.Mesh(
      t.id === 'bowl'
        ? new THREE.SphereGeometry(t.width / 2, 22, 16)
        : new THREE.BoxGeometry(t.width, t.height, t.depth),
      new THREE.MeshStandardMaterial({
        color: 0xcfeaff, transparent: true, opacity: 0.17, roughness: 0.05,
        metalness: 0.1, side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    glass.position.y = midY;
    glass.renderOrder = 6;
    this.group.add(glass);
    this.glass = glass;

    const gravel = new THREE.Mesh(
      new THREE.BoxGeometry(t.width - 0.07, 0.05, t.depth - 0.07),
      new THREE.MeshStandardMaterial({ color: 0xa8946f, roughness: 1 }),
    );
    gravel.position.y = t.standHeight + (t.id === 'bowl' ? t.height * 0.2 : 0.05);
    this.group.add(gravel);

    if (t.id !== 'bowl') {
      const rim = new THREE.Mesh(
        new THREE.BoxGeometry(t.width + 0.05, 0.06, t.depth + 0.05),
        new THREE.MeshStandardMaterial({ color: 0x2a2f3d, roughness: 0.6, metalness: 0.4 }),
      );
      rim.position.y = t.standHeight + t.height;
      this.group.add(rim);
    }

    // Somewhere for tank decorations to stand, spread along the gravel.
    for (let i = 0; i < t.decorAnchors; i++) {
      const o = new THREE.Object3D();
      const k = t.decorAnchors === 1 ? 0.5 : i / (t.decorAnchors - 1);
      o.position.set((k - 0.5) * (t.width - 0.3), gravel.position.y + 0.025, -t.depth * 0.12);
      o.name = `tank-decor-${i}`;
      this.group.add(o);
      this.decorAnchors.push(o);
    }

    if (this.readOnly) return;
    pointer.register(glass, {
      id: `${this._idPrefix}:glass`,
      group: 'tank',
      label: 'Fish tank',
      onSelect: () => this._hideCard(),
    });
  }

  /** @private */
  _buildPlaques() {
    const t = this.tier;
    this._fullSign = factory.createSign('', {
      w: 1.0, h: 0.22, lines: ['TANK FULL — UPGRADE!'], size: 30, bg: '#3a1d1d',
    });
    // The tank group is rotated PI, so its local +Z is the room-facing side.
    // Both plaques belong there, unrotated — see the note in shelves.js.
    this._fullSign.position.set(0, t.standHeight - 0.16, t.depth / 2 + 0.09);
    this._fullSign.rotation.y = 0;
    this._fullSign.visible = false;
    this.group.add(this._fullSign);

    if (this.readOnly) return;
    const next = nextTankTier(this.model.tankCapacity);
    const plate = factory.createSign('', {
      w: 0.94, h: 0.22, size: 30,
      lines: [next ? `BIGGER TANK  ${next.capacity} FISH  ${next.cost}*` : 'BIGGEST TANK'],
      bg: next ? '#1d3a2e' : '#232838',
    });
    plate.position.set(0, t.standHeight * 0.45, t.depth / 2 + 0.09);
    plate.rotation.y = 0;
    this.group.add(plate);
    if (next) {
      pointer.register(plate, {
        id: `${this._idPrefix}:upgrade`,
        group: 'tank',
        label: 'Upgrade tank',
        onSelect: () => this.onUpgrade?.(),
      });
    }
  }

  // ── Stocking ──────────────────────────────────────────────────────────────

  /** @private */
  _stock(records) {
    const cap = this.model.tankCapacity;
    this.waiting = records.slice(cap);
    if (this._fullSign) this._fullSign.visible = this.waiting.length > 0;

    records.slice(0, cap).forEach((record, i) => this._addFish(record, i));
  }

  /** @private */
  _addFish(record, index) {
    const obj = factory.createFish(record.variant);
    const variant = fishVariant(record.variant);

    // The factory already hangs the tail on its own pivot group, which is what
    // lets it flick without dragging the body round with it.
    const tail = obj.getObjectByName('tail') || null;

    obj.traverse((c) => {
      if (!c.isMesh) return;
      // Materials are memoised and shared across every fish, so tinting one
      // in place would tint the shoal.
      c.material = c.material.clone();
      // Only materials that already have an emissive channel get one set. The
      // fish's eye glint is a MeshBasicMaterial, whose shader has no `emissive`
      // uniform — assigning the property anyway makes three.js dereference a
      // uniform that does not exist, and the render loop dies mid-frame.
      if (variant.rarity !== 'common' && c.material.emissive) {
        c.material.emissive.set(variant.color);
        c.material.emissiveIntensity = variant.rarity === 'rare' ? 0.5 : 0.22;
      }
    });

    const scale = record.variant === 'golden' ? 1.25 : 1;
    obj.scale.setScalar(scale * (this.tier.id === 'bowl' ? 0.8 : 1));
    this.group.add(obj);

    const rec = {
      record, object3D: obj, tail, index,
      seed: swimSeed(index),
      variant,
      hitbox: null,
      nextSplash: 0,
    };

    // The fish mesh is tiny; a generous invisible sphere is what makes it
    // pickable at all with a crosshair.
    const hit = new THREE.Mesh(
      new THREE.SphereGeometry(0.075, 8, 6),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    obj.add(hit);
    rec.hitbox = hit;

    this.fish.push(rec);

    pointer.register(hit, {
      id: `${this._idPrefix}:fish:${record.id}`,
      group: 'fish',
      label: this.displayName(record),
      onHover: (on) => this._hoverFish(on ? rec : null),
      onSelect: () => this.showCard(record.id),
    });
  }

  /**
   * The name a fish shows, falling back to the GDD's default.
   * @param {object} record
   * @returns {string}
   */
  displayName(record) {
    if (record.name) return record.name;
    const n = (this.model?.fish || []).indexOf(record);
    return `Fish #${(n < 0 ? 0 : n) + 1}`;
  }

  // ── Labels and the info card ──────────────────────────────────────────────

  /** @private */
  _hoverFish(rec) {
    this._focus = rec;
    if (!rec) {
      if (this._label) this._label.visible = false;
      return;
    }
    if (!this._label) {
      this._label = createLabel('', { scale: 0.26 });
      this.room.root.add(this._label);
    }
    this._label.userData.setText(this.displayName(rec.record));
    this._label.visible = true;
  }

  /**
   * Show the info card for a fish (GDD 9.3 "tap to see fish names and stats").
   * @param {string} fishId
   * @returns {boolean}
   */
  showCard(fishId) {
    const rec = this.fish.find((f) => f.record.id === fishId);
    if (!rec) return false;
    this._hideCard();

    const info = this.info(fishId);
    this._card = createPanel({
      title: info.name, width: 1.05, height: 0.62, align: 'left',
      lines: [
        `Variety   ${info.variantName}`,
        `Caught    day ${info.day}`,
        `Won at    ${info.stallName}`,
      ],
    });
    const t = this.tier;
    // Same convention as the plaques: the tank group is rotated PI, so local +Z
    // is the side the player stands on and an unrotated panel faces them.
    this._card.position.set(0, t.standHeight + t.height + 0.45, 0.25);
    this._card.rotation.y = 0;
    this.group.add(this._card);
    this._cardFor = fishId;

    if (!this.readOnly && this.onRename) {
      const btn = createPanel({
        width: 0.44, height: 0.15, lines: ['RENAME'],
        bg: '#1d3a2e', accent: 'rgba(111,231,196,0.8)', radius: 14,
      });
      btn.position.set(0.26, -0.36, 0.004);
      this._card.add(btn);
      pointer.register(btn, {
        id: `${this._idPrefix}:rename`,
        group: 'tank',
        label: 'Rename',
        onHover: (on) => btn.setStyle({ bg: on ? '#2b5c47' : '#1d3a2e' }),
        onSelect: () => this.onRename(fishId),
      });
    }
    return true;
  }

  /** @private */
  _hideCard() {
    if (!this._card) return;
    pointer.unregister(`${this._idPrefix}:rename`);
    this._card.parent?.remove(this._card);
    this._card.dispose?.();
    this._card = null;
    this._cardFor = null;
  }

  // ── Queries ───────────────────────────────────────────────────────────────

  /** @returns {Array<object>} Every displayed fish with its live position. */
  list() {
    return this.fish.map((f) => ({
      id: f.record.id,
      name: this.displayName(f.record),
      variant: f.record.variant,
      stallId: f.record.stallId,
      day: f.record.day,
      position: f.object3D.position.toArray().map((n) => Number(n.toFixed(4))),
    }));
  }

  /** @returns {Array<{id, variant, name}>} Fish the tank is too small to show. */
  waitlist() {
    return this.waiting.map((r) => ({ id: r.id, variant: r.variant, name: r.name || null }));
  }

  /** @param {string} fishId @returns {object|null} */
  info(fishId) {
    const rec = this.fish.find((f) => f.record.id === fishId)
      || { record: this.waiting.find((r) => r.id === fishId), variant: null };
    if (!rec.record) return null;
    const v = fishVariant(rec.record.variant);
    return {
      id: rec.record.id,
      name: this.displayName(rec.record),
      variant: rec.record.variant,
      variantName: v.name,
      rarity: v.rarity,
      day: rec.record.day,
      stallId: rec.record.stallId,
      stallName: STALL_BY_ID[rec.record.stallId]?.name || 'a mystery stall',
      displayed: !!this.fish.find((f) => f.record.id === fishId),
    };
  }

  /** @returns {string|null} The fish the player is currently looking at. */
  focusedId() {
    return this._focus ? this._focus.record.id : null;
  }

  /** @returns {string|null} */
  cardFor() {
    return this._cardFor;
  }

  /**
   * Where a fish sits at an arbitrary time — the deterministic core, exposed so
   * a test can assert reproducibility without racing the frame clock.
   * @param {string} fishId
   * @param {number} t Seconds.
   * @returns {[number, number, number]|null} Tank-local position.
   */
  sample(fishId, t) {
    const rec = this.fish.find((f) => f.record.id === fishId);
    if (!rec) return null;
    const p = this._positionAt(rec, t);
    return [p.x, p.y, p.z];
  }

  /** @private @returns {THREE.Vector3} */
  _positionAt(rec, t, out = new THREE.Vector3()) {
    return swimPosition(rec.seed, t, this.centre, this.bounds, out);
  }

  // ── Frame ─────────────────────────────────────────────────────────────────

  update(dt, camera) {
    this._elapsed += dt;
    const t = this._elapsed;
    const ahead = new THREE.Vector3();

    for (const rec of this.fish) {
      const p = this._positionAt(rec, t, rec.object3D.position);
      this._positionAt(rec, t + 0.06, ahead);
      const dx = ahead.x - p.x;
      const dz = ahead.z - p.z;
      if (dx || dz) {
        // createFish points down −X, so a heading of (dx,dz) is atan2(dz, −dx).
        rec.object3D.rotation.y = Math.atan2(dz, -dx);
      }
      rec.object3D.rotation.z = Math.max(-0.4, Math.min(0.4, (ahead.y - p.y) * 6));

      const dart = dartAmount(rec.seed, t);
      if (rec.tail) rec.tail.rotation.y = Math.sin(t * rec.seed.wiggle * (1 + dart * 2)) * (0.35 + dart * 0.4);

      // A dart near the surface breaks the water.
      if (dart > 0.72 && p.y > this.centre.y + this.bounds.y * 0.5 && t > rec.nextSplash) {
        rec.nextSplash = t + 3;
        this._splashes.spawn(p.x, this.centre.y + this.bounds.y, p.z);
      }
    }

    this._updateLabel(camera);
    this._splashes.update(dt);
  }

  /** @private */
  _updateLabel(camera) {
    if (!this._label || !this._label.visible || !this._focus) return;
    const p = this._focus.object3D.position;
    this._label.position.set(p.x, p.y + 0.16, p.z);
    this.group.localToWorld(this._label.position);
    this.room.root.worldToLocal(this._label.position);
    if (camera) {
      // Fade out when you walk away rather than popping off.
      const world = this._label.getWorldPosition(new THREE.Vector3());
      const d = world.distanceTo(camera.getWorldPosition(new THREE.Vector3()));
      this._label.material.opacity = d > 7 ? 0 : Math.min(1, (7 - d) / 1.5);
    }
  }

  dispose() {
    this._hideCard();
    for (const f of this.fish) pointer.unregister(`${this._idPrefix}:fish:${f.record.id}`);
    pointer.unregister(`${this._idPrefix}:upgrade`);
    pointer.unregister(`${this._idPrefix}:glass`);
    this._label?.userData.dispose?.();
    this._label = null;
    this._focus = null;
    this.fish.length = 0;
    this.waiting.length = 0;
    this.decorAnchors.length = 0;
    this._splashes.clear();
    while (this.group.children.length) this.group.remove(this.group.children[0]);
  }
}
